import React, { useState } from 'react';
import { Helmet } from 'react-helmet';

const FreeTrial = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Free Healthcare RCM Trial",
    "provider": {
      "@type": "Organization",
      "name": "MDeRCM"
    },
    "description": "Free trial of AI-powered healthcare revenue cycle management services",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  return (
    <>
      <Helmet>
        <title>Free RCM Trial | Healthcare Billing Services | MDeRCM</title>
        <meta 
          name="description" 
          content="Start your free RCM trial. AI-powered medical billing, claims processing & denial management. No setup fees, no commitment required." 
        />
        <meta 
          name="keywords" 
          content="free RCM trial, healthcare revenue cycle management free trial, medical billing outsourcing trial, RCM services demo, free healthcare automation trial" 
        />
        <link rel="canonical" href="https://mdercm.com/free-trial" />
        
        <meta property="og:title" content="Free Trial | AI-Powered Healthcare RCM Services | MDeRCM" />
        <meta property="og:description" content="Experience AI-powered revenue cycle management free. No setup fees, no commitment. See results in weeks." />
        <meta property="og:url" content="https://mdercm.com/free-trial" />
        <meta property="og:type" content="website" />
        
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      </Helmet>

      <div style={styles.wrapper}>
        <div style={styles.container}>
          {/* Left Section */}
          <div style={styles.leftSection}>
            <h1 style={styles.title}>Experience AI-Powered RCM – On Us</h1>
            <p style={styles.subheading}>
              Schedule a free trial today and discover how our AI Agents can transform your revenue operations.
            </p>
            <p style={styles.tagline}>Empower your healthcare business with automation and insight.</p>
            <ul style={styles.bulletList}>
              <li style={styles.bulletItem}>✅ Eliminate repetitive admin work with intelligent AI task automation</li>
              <li style={styles.bulletItem}>✅ Boost operational efficiency and protect your bottom line</li>
              <li style={styles.bulletItem}>✅ Improve accuracy and turnaround times across all workflows</li>
              <li style={styles.bulletItem}>✅ Zero setup fees – only pay when you're ready to scale</li>
            </ul>
          </div>

          {/* Right Section */}
          <div style={styles.rightSection}>
            <form
              style={styles.form}
              onSubmit={async (e) => {
                e.preventDefault();
                setIsSubmitting(true);
                
                const form = e.target;
                const payload = {
                  firstName: form[0].value,
                  lastName: form[1].value,
                  companyWebsite: form[2].value,
                  title: form[3].value,
                  email: form[4].value,
                  phone: form[5].value,
                  message: form[6].value,
                };

                console.log('📤 Submitting form data:', payload);

                try {
                  // Create FormData object
                  const formData = new FormData();
                  formData.append('firstName', payload.firstName);
                  formData.append('lastName', payload.lastName);
                  formData.append('companyWebsite', payload.companyWebsite);
                  formData.append('title', payload.title);
                  formData.append('email', payload.email);
                  formData.append('phone', payload.phone);
                  formData.append('message', payload.message);

                  console.log('🌐 Sending request to Google Apps Script...');

                  // Send data to Google Apps Script
                  const response = await fetch("https://script.google.com/macros/s/AKfycbzKXBqFZuVJlJ2fT0PCEEp0fdWbVwsCqB10SDP8IMQOyGRExKHeotlkcMPKTRol0dpy/exec", {
                    method: "POST",
                    body: formData,
                    mode: 'no-cors' // Required for Google Apps Script
                  });

                  console.log('✅ Response received:', {
                    type: response.type,
                    status: response.status
                  });

                  // With 'no-cors' mode, we can't read the response
                  // But if no error was thrown, the request was successful
                  alert("✅ Form submitted successfully! We'll contact you soon.");

                  // Google Ads Conversion Tracking
                  if (typeof window.gtag !== 'undefined') {
                    window.gtag('event', 'conversion', {
                      'send_to': 'AW-17494976074/2X7dCJ6euJUbEMrMoJZB',
                      'value': 3000,
                      'currency': 'INR'
                    });
                    console.log('📊 Google Ads conversion tracked');
                  }

                  // Reset form
                  form.reset();
                  
                } catch (err) {
                  console.error('❌ Submission error:', err);
                  alert("❌ Submission failed. Please try again or call us at +1 (510) 356-6069");
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              <input type="text" name="firstName" placeholder="First Name*" style={styles.input} required />
              <input type="text" name="lastName" placeholder="Last Name*" style={styles.input} required />
              <input type="text" name="companyWebsite" placeholder="Company Website*" style={styles.input} required />
              <input type="text" name="title" placeholder="Title*" style={styles.input} required />
              <input type="email" name="email" placeholder="Email*" style={styles.input} required />
              <input type="tel" name="phone" placeholder="Phone*" style={styles.input} required />
              <textarea name="message" placeholder="How can we help?" style={{ ...styles.input, minHeight: '100px' }}></textarea>
              <button 
                type="submit" 
                style={{
                  ...styles.button,
                  backgroundColor: isSubmitting ? '#ccc' : '#F26522',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer'
                }}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'SUBMITTING...' : 'SUBMIT'}
              </button>
            </form>
            <p style={styles.support}>Need help now? Call <strong>+1 (510) 356-6069</strong></p>
            <p style={styles.footerNote}>
              Discover how MDeRCM's AI solutions streamline workflows and accelerate claim outcomes.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

const styles = {
  wrapper: {
    fontFamily: "'Poppins', sans-serif",
    background: 'linear-gradient(to right, #0072CE, #00B050)',
    minHeight: '100vh',
    padding: '40px 0',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    maxWidth: '1440px',
    width: '95%',
    background: '#fff',
    borderRadius: '10px',
    overflow: 'hidden',
    boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
  },
  leftSection: {
    flex: '1 1 50%',
    background: 'linear-gradient(to bottom, #0072CE, #00B050)',
    color: '#fff',
    padding: '40px',
    minHeight: '100%',
  },
  title: {
    fontSize: '38px',
    fontWeight: 'bold',
    marginBottom: '10px',
  },
  subheading: {
    fontSize: '16px',
    marginBottom: '20px',
  },
  tagline: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '30px',
  },
  bulletList: {
    listStyle: 'none',
    padding: 0,
    fontSize: '16px',
  },
  bulletItem: {
    marginBottom: '14px',
  },
  rightSection: {
    flex: '1 1 50%',
    padding: '40px 30px',
    backgroundColor: '#fff',
    color: '#000',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  input: {
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '6px',
    fontSize: '14px',
  },
  button: {
    padding: '12px',
    background: '#F26522',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontWeight: 'bold',
    fontSize: '15px',
    cursor: 'pointer',
    marginTop: '10px',
  },
  support: {
    textAlign: 'center',
    marginTop: '25px',
    fontWeight: 600,
    color: '#0072CE',
  },
  footerNote: {
    textAlign: 'center',
    fontSize: '12px',
    marginTop: '10px',
    color: '#444',
  },
};

export default FreeTrial;